import { useState, useEffect } from 'react';
import { Loader2, Sparkles, FileText } from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { billRetailerService, type BvParseRetailer } from '../../services/billRetailerService';

type RetailerPromptPickerProps = {
  value: string | null;
  onChange: (retailerId: string | null) => void;
  /**
   * The retailer name recorded on the site (`site.current_retailer`). Used only
   * to pre-select the dropdown when the bill has no retailer of its own.
   */
  siteRetailerName?: string | null;
  disabled?: boolean;
  className?: string;
};

/**
 * Which retailer's parsing prompt to use for this invoice.
 *
 * The dropdown is the mechanism, not a convenience: a bill has no retailer of
 * its own, and `site.current_retailer` is free text that matches a real retailer
 * row about half the time by distinct value. So a person picks, and the choice
 * is stored on the bill.
 *
 * The pre-selection from the site name is a suggestion and nothing more — it
 * fills the box, and a parse only ever uses a custom prompt because somebody
 * left it there.
 *
 * Whether the chosen retailer actually *has* a usable prompt is shown before the
 * parse runs. "Custom prompt" versus "Standard prompt" after the fact is no use
 * when the question is why two invoices extracted differently.
 */
export const RetailerPromptPicker = ({
  value, onChange, siteRetailerName, disabled = false, className,
}: RetailerPromptPickerProps) => {
  const [retailers, setRetailers] = useState<BvParseRetailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const res = await billRetailerService.list();
      if (!mounted) return;
      if (res.success) {
        setRetailers(res.data ?? []);
        setError(null);
      } else {
        setError(res.error ?? 'Could not load retailers');
      }
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  // Pre-select from the site's recorded retailer, but only while nothing is
  // chosen — never overwrite a selection the user (or a previous parse) made.
  useEffect(() => {
    if (value || loading || !siteRetailerName?.trim() || retailers.length === 0) return;
    const match = retailers.find(
      (r) => r.name.trim().toLowerCase() === siteRetailerName.trim().toLowerCase(),
    );
    if (match) onChange(match.id);
    // onChange intentionally omitted: re-running on every parent render would
    // fight a user who deliberately cleared the box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, loading, siteRetailerName, retailers]);

  const selected = retailers.find((r) => r.id === value) ?? null;
  const unmatchedSiteName =
    !loading && !value && !!siteRetailerName?.trim()
    && !retailers.some((r) => r.name.trim().toLowerCase() === siteRetailerName.trim().toLowerCase());

  return (
    <div className={cn('space-y-1', className)}>
      <label className="block text-xs font-medium text-gray-600">Retailer (parsing prompt)</label>

      <div className="relative">
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled || loading}
          className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 bg-white disabled:bg-gray-50"
        >
          <option value="">Standard prompt (no retailer)</option>
          {retailers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}{r.has_bill_prompt ? ' — custom prompt' : ''}
            </option>
          ))}
        </select>
        {loading && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 absolute right-7 top-2.5 pointer-events-none" />
        )}
      </div>

      {error ? (
        <p className="text-[11px] text-red-600">{error} — parsing will use the standard prompt.</p>
      ) : selected?.has_bill_prompt ? (
        <p className="text-[11px] text-blue-600 flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> Will parse with <strong>{selected.name}</strong>'s custom prompt.
        </p>
      ) : selected ? (
        <p className="text-[11px] text-gray-400 flex items-center gap-1">
          <FileText className="h-3 w-3" /> {selected.name} has no custom bill prompt — standard prompt will be used.
        </p>
      ) : unmatchedSiteName ? (
        <p className="text-[11px] text-amber-600">
          The site records &ldquo;{siteRetailerName}&rdquo;, which isn&rsquo;t set up as a retailer.
          Pick one, or leave it on the standard prompt.
        </p>
      ) : (
        <p className="text-[11px] text-gray-400 flex items-center gap-1">
          <FileText className="h-3 w-3" /> Standard prompt.
        </p>
      )}
    </div>
  );
};
